import React, { useRef, useState, useCallback, useEffect, useContext } from 'react';
import sanitizeHtml from 'sanitize-html';
import { ServerConfigContext } from '../context/ServerConfigContext';
import { JWTAuthContext } from '../context/JWTAuthContext';
import './RichTextEditor.css';

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = '내용을 입력하세요...', 
  disabled = false,
  height = '400px' 
}) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const { getServer } = useContext(ServerConfigContext);
  const { jwtToken } = useContext(JWTAuthContext);

  // 텍스트 포맷팅 명령어 실행
  const execCommand = useCallback((command, value = null) => {
    if (disabled) return;
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  }, [disabled]);

  // 단순한 에디터 변경 핸들러
  const handleEditorChange = useCallback(() => {
    if (!editorRef.current || disabled) return;
    
    const content = editorRef.current.innerHTML;
    onChange?.(content);
  }, [disabled, onChange]);

  // 이미지 업로드 함수
  const uploadImage = useCallback(async (file) => {
    if (disabled) return;

    // 파일 크기 검증 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('이미지 파일 크기는 10MB 이하여야 합니다.');
      return;
    }

    // 파일 타입 검증
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('jpg, png, webp 형식의 이미지만 업로드 가능합니다.');
      return;
    }

    try {
      // board-api-server 설정 가져오기
      const boardServer = getServer('board-api-server');
      if (!boardServer || !boardServer.isActive) {
        throw new Error('게시판 서버가 활성화되지 않았습니다.');
      }

      const formData = new FormData();
      formData.append('file', file);

      const apiUrl = `/board-api/api/v1/images/upload`;

      console.log('📤 [RichTextEditor] 이미지 업로드 시작:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        serverUrl: 'proxy',
        apiUrl: apiUrl
      });
      
      // Authorization 헤더 생성 (JWT 토큰 우선 사용)
      const headers = {
        'Accept': 'application/json'
      };

      // JWT 토큰이 있으면 우선 사용, 없으면 기존 AuthContext 토큰 사용
      const authToken = jwtToken || localStorage.getItem('auth_token');

      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
        console.log('🔑 [RichTextEditor] Authorization 헤더 설정:', {
          tokenSource: jwtToken ? 'JWT' : 'AuthContext',
          tokenLength: authToken.length,
          tokenStart: authToken.substring(0, 20) + '...'
        });
      } else {
        console.warn('⚠️ [RichTextEditor] 인증 토큰이 없습니다.');
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [RichTextEditor] 업로드 실패 응답:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`이미지 업로드에 실패했습니다: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ [RichTextEditor] 업로드 성공:', result);

      if (result.success && result.data && result.data.original_url) {
        let imageUrl = result.data.original_url;

        // localhost URL을 프록시 경로로 변환
        if (imageUrl.startsWith('http://localhost:8080')) {
          // http://localhost:8080/uploads/images/xxx.png → /board-api/uploads/images/xxx.png
          const imagePath = imageUrl.replace('http://localhost:8080', '');
          imageUrl = `/board-api${imagePath}`;
          console.log('🔄 [RichTextEditor] URL 변환 (프록시 경로):', {
            original: result.data.original_url,
            converted: imageUrl
          });
        }

        // 에디터에 이미지 삽입
        const imgTag = `<img src="${imageUrl}" alt="Uploaded image" style="max-width: 100%; height: auto;" />`;
        document.execCommand('insertHTML', false, imgTag);
        editorRef.current?.focus();

        console.log('🖼️ [RichTextEditor] 이미지 삽입 완료:', imageUrl);
      } else {
        throw new Error('서버에서 이미지 URL을 반환하지 않았습니다.');
      }
    } catch (error) {
      console.error('❌ [RichTextEditor] 이미지 업로드 실패:', error);
      alert(`이미지 업로드에 실패했습니다: ${error.message}`);
    }
  }, [disabled, getServer, jwtToken]);

  // 파일 선택 핸들러
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadImage(file);
    }
    // input 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadImage]);

  // 드래그 앤 드롭 핸들러
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find(file => file.type.startsWith('image/'));
    
    if (imageFile) {
      uploadImage(imageFile);
    }
  }, [uploadImage]);

  // 붙여넣기 핸들러
  const handlePaste = useCallback((e) => {
    if (disabled) return;

    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    
    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) {
        uploadImage(file);
      }
    }
  }, [disabled, uploadImage]);

  // 키보드 단축키 처리만
  const handleKeyDown = useCallback((e) => {
    if (disabled) return;

    // 키보드 단축키 처리
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
        default:
          break;
      }
    }
  }, [disabled, execCommand]);

  // 링크 삽입
  const insertLink = useCallback(() => {
    if (disabled) return;
    
    const url = prompt('링크 URL을 입력하세요:');
    if (url) {
      execCommand('createLink', url);
    }
  }, [disabled, execCommand]);

  // 코드 블록 삽입
  const insertCodeBlock = useCallback(() => {
    if (disabled) return;
    
    const code = `<pre><code>${getSelection().toString() || '코드를 입력하세요'}</code></pre>`;
    document.execCommand('insertHTML', false, code);
    editorRef.current?.focus();
  }, [disabled]);

  // 체크리스트 삽입
  const insertChecklist = useCallback(() => {
    if (disabled) return;
    
    const checklistItem = `<div><input type="checkbox" disabled> 체크리스트 항목</div>`;
    document.execCommand('insertHTML', false, checklistItem);
    editorRef.current?.focus();
  }, [disabled]);

  // 외부에서 초기값 설정시에만 에디터 내용 업데이트
  useEffect(() => {
    if (editorRef.current && value && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  return (
    <div className={`rich-text-editor ${disabled ? 'disabled' : ''}`}>
      {/* 툴바 */}
      <div className="editor-toolbar">
        <div className="toolbar-group">
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('bold')}
            disabled={disabled}
            title="굵게 (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('italic')}
            disabled={disabled}
            title="기울임 (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('underline')}
            disabled={disabled}
            title="밑줄 (Ctrl+U)"
          >
            <u>U</u>
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('strikeThrough')}
            disabled={disabled}
            title="취소선"
          >
            <s>S</s>
          </button>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <select 
            className="toolbar-select"
            onChange={(e) => execCommand('formatBlock', e.target.value)}
            disabled={disabled}
            defaultValue=""
          >
            <option value="">일반</option>
            <option value="<h1>">제목 1</option>
            <option value="<h2>">제목 2</option>
            <option value="<h3>">제목 3</option>
            <option value="<blockquote>">인용문</option>
          </select>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('insertUnorderedList')}
            disabled={disabled}
            title="불릿 리스트"
          >
            • 목록
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('insertOrderedList')}
            disabled={disabled}
            title="번호 리스트"
          >
            1. 목록
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={insertChecklist}
            disabled={disabled}
            title="체크리스트"
          >
            ☑ 체크
          </button>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button 
            type="button"
            className="toolbar-btn"
            onClick={insertLink}
            disabled={disabled}
            title="링크 삽입"
          >
            🔗 링크
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('insertHTML', '<code>코드</code>')}
            disabled={disabled}
            title="인라인 코드"
          >
            &lt;/&gt;
          </button>
          <button 
            type="button"
            className="toolbar-btn"
            onClick={insertCodeBlock}
            disabled={disabled}
            title="코드 블록"
          >
            {} 블록
          </button>
        </div>

        <div className="toolbar-divider" />

        <div className="toolbar-group">
          <button 
            type="button"
            className="toolbar-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            title="이미지 업로드"
          >
            🖼️ 이미지
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* 에디터 */}
      <div 
        ref={editorRef}
        className={`editor-content ${isDragging ? 'dragging' : ''}`}
        contentEditable={!disabled}
        suppressContentEditableWarning={true}
        style={{ height }}
        onInput={handleEditorChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-placeholder={placeholder}
      />

      {isDragging && (
        <div className="drag-overlay">
          <div className="drag-message">
            <div className="drag-icon">📁</div>
            <p>이미지를 여기에 드롭하세요</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;